import Script from 'next/script';
import { disableGtm, enableGtm, getGtmScriptSrc, isGtmEnabled } from '@/lib/analytics/gtm/client';
import { useGtmEnabled } from '@/lib/analytics/gtm/useGtmEnabled';
import { getConsent, useConsentHandler } from '@/lib/consent';

/**
 * Consent-gated Google Tag Manager: GTM enables and tears itself down via the consent registry without the consent provider knowing GTM exists.
 *
 * - Before Accept (GDPR): gtm.js does not load, the dataLayer is not initialised, and dataLayer pushes are inert — no GTM/GA cookies are created.
 * - On withdraw/Reject: disableGtm() stops pushes, drops the dataLayer, and clears GTM/GA cookies.
 * - Off-GDPR: behaviour is unchanged (consent is always "allowed").
 *
 * The `<noscript>` fallback iframe (no-JS users) is gated separately in `_document.tsx`, which can only decide from the request cookie.
 */
const GtmScript = () => {
  useConsentHandler('gtm', ({ allowed }) => {
    if (allowed) {
      enableGtm();
      return;
    }

    // Only reload if GTM was enabled this session; a pre-consent reject never injected gtm.js, so there is no runtime to drop.
    const wasEnabled = isGtmEnabled();
    disableGtm();

    // The loaded gtm.js Reddit tag re-creates `_rdt_em` after we clear it and can't be stopped in place. Reloading drops the runtime; consent is now rejected so gtm.js won't reload, and the post-reload teardown clears the cookie for good. Next tick so the synchronous consent fan-out finishes first, then re-check live consent: a quick re-accept re-enables GTM in place, so a reload would be a needless disruption.
    if (wasEnabled && typeof window !== 'undefined') {
      window.setTimeout(() => {
        if (!getConsent().allowed) {
          window.location.reload();
        }
      }, 0);
    }
  });

  const gtmEnabled = useGtmEnabled();
  const gtmScriptSrc = gtmEnabled ? getGtmScriptSrc() : null;

  if (!gtmScriptSrc) {
    return null;
  }

  return <Script id="gtm-script" strategy="afterInteractive" src={gtmScriptSrc} />;
};

export default GtmScript;
